import {makeAutoObservable, runInAction} from 'mobx';
import {useCallback, useRef, useState} from 'react';

import {updateWindow} from './pagination';
import {
  CommonRequestOptions,
  defaultPaginationArguments,
  PageItem,
  PaginatedEndpointArguments,
  PaginatedLoadable,
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
  PaginationResponse,
  State,
} from './types';

type ResponseWithBody<S extends number, T> = {
  status: S;
  body: T;
};

type PagedFetchCallType<T, V extends object> = (
  paginationArguments: Omit<PaginationArguments, 'cursor'>,
  old: PaginatedLoadable<T>,
  additionalProperties: V
) => Promise<PaginatedLoadable<T>>;

export type PaginatedItemLoaderApi<
  T extends {cursor?: string}[],
  V extends object,
> = {
  loadable: PaginatedLoadable<T>;
  fetch: (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    additionalProperties: V
  ) => Promise<void>;
  fetchMore: (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    additionalProperties: V
  ) => Promise<void>;
  fetchIds: (ids: string[] | undefined) => Promise<PaginatedLoadable<T>>;
};

export class PaginatedItemLoader<
  T extends {cursor?: string}[],
  V extends object,
> {
  public loadable: PaginatedLoadable<T>;
  private readonly fetchCall: PagedFetchCallType<T, V>;

  public constructor(fetchCall: PagedFetchCallType<T, V>) {
    makeAutoObservable(this, {}, {autoBind: true});

    this.loadable = {
      hasMoreAfter: false,
      hasMoreBefore: false,
      state: State.Loading,
    };
    this.fetchCall = fetchCall;
  }

  /**
   * Fetches and replaces all items with given additional properties, e.g. query
   */
  public async fetch(
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    additionalProperties: V
  ): Promise<void> {
    const newLoadable = await this.fetchCall(
      paginationArguments,
      {...this.loadable, data: undefined, hasMoreBefore: false},
      updateQueryParamForApi(additionalProperties)
    );
    runInAction(() => {
      this.loadable = newLoadable;
    });
  }

  /**
   * Fetches more items with given additional properties, e.g. query.
   * The new items will be appended to loadable, not replaced.
   */
  public async fetchMore(
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    additionalProperties: V
  ): Promise<void> {
    const newLoadable = await this.fetchCall(
      paginationArguments,
      this.loadable,
      updateQueryParamForApi(additionalProperties)
    );
    runInAction(() => {
      this.loadable = newLoadable;
    });
  }

  public fetchIds(ids: string[] | undefined): Promise<PaginatedLoadable<T>> {
    return this.fetchCall(
      {
        mode: PaginationMode.Browse,
        direction: PaginationDirection.Forward,
      },
      // old loadable doesn't matter here
      {
        state: State.Success,
        hasMoreAfter: false,
        hasMoreBefore: false,
      },
      {searchQuery: '', ids} as V
    );
  }
}

const hasQueryParam = (params: object): params is {query: string} =>
  params !== undefined && (params as {query: string}).query !== undefined;

const updateQueryParamForApi = <U extends object>(params: U): U => {
  let updatedArguments: U = {...params};
  if (hasQueryParam(params)) {
    const {query} = params;
    // make sure undefined is sent to the BE instead of an empty string or null
    updatedArguments = {...params, query: query || undefined};
  }
  return updatedArguments;
};

export const convertToPaginatedEndpointArguments = ({
  mode,
  cursor,
  direction,
  key,
  sortFields,
}: PaginationArguments): PaginatedEndpointArguments => ({
  paginationCursor: cursor || undefined,
  paginationDirection: direction || defaultPaginationArguments.direction,
  paginationKeyComponents: key,
  paginationMode: mode || defaultPaginationArguments.mode,
  paginationSortFieldValues: sortFields,
});

/**
 * @param otherArguments if the param 'query' is contained, this method also makes sure to send undefined to the backend instead of an empty string / null
 */
export const fetchPaginatedItems = <T, U extends object>(
  endpointFunction: (args: PaginatedEndpointArguments & U) => T,
  paginationArguments: PaginationArguments,
  /** if the param 'query' is contained, it will be converted to undefined if it's an empty string / null */
  otherArguments?: Omit<U, keyof PaginatedEndpointArguments>
): T =>
  endpointFunction({
    ...convertToPaginatedEndpointArguments(paginationArguments),
    ...(otherArguments && updateQueryParamForApi(otherArguments)),
  } as PaginatedEndpointArguments & U);

export type HandledArgs<OTHER_PARAMS extends object> = {
  parameters: PaginatedEndpointArguments & CommonRequestOptions & OTHER_PARAMS;
  abortCallbackSetter?: (abortCallback: () => void) => void;
};

export type HandledResponseType<HANDLED_BODY> = {
  body: ResponseWithBody<number, HANDLED_BODY> | undefined;
  hasError: boolean;
};

type EndpointInformation<
  OTHER_PARAMS extends object,
  HANDLED_BODY,
  ITEM_TYPE,
> = {
  /** The handled backend endpoint function for the paginated call. */
  endpointFunction: (
    args: HandledArgs<OTHER_PARAMS>
  ) => Promise<HandledResponseType<HANDLED_BODY>>;
  /**
   * Other parameters necessary for the endpoint function include all parameters except the pagination and common request parameters.
   * If the param 'query' is contained, this method also makes sure to send undefined to the backend instead of an empty string / null.
   */
  otherParams: OTHER_PARAMS;
  /** Function to extract the pagination response from the body of the response. */
  getPageItemFromResponse: (
    body: HANDLED_BODY
  ) => PaginationResponse<PageItem<ITEM_TYPE>>;
};

/**
 * Fetch new paginated items with the paginationArguments and endpoint provided and return a new loadable with the items.
 */
export const handlePaginatedLoadableFetch = async <
  OTHER_PARAMS extends object,
  HANDLED_BODY,
  ITEM_TYPE,
>(
  {
    endpointFunction,
    otherParams,
    getPageItemFromResponse,
  }: EndpointInformation<OTHER_PARAMS, HANDLED_BODY, ITEM_TYPE>,
  paginationArguments: Omit<PaginationArguments, 'cursor'>,
  /** The current loadable used to get the start/end point of the page and to abort any currently running requests. */
  currentLoadable: PaginatedLoadable<PageItem<ITEM_TYPE>[]>
): Promise<PaginatedLoadable<PageItem<ITEM_TYPE>[]>> => {
  currentLoadable.abort?.();
  const cursor =
    paginationArguments.direction === PaginationDirection.Backward
      ? currentLoadable.data?.[0]?.cursor
      : currentLoadable.data?.[currentLoadable.data.length - 1]?.cursor;
  const pageArgs: PaginationArguments = {
    ...paginationArguments,
    cursor,
  } as PaginationArguments;

  const otherParameters = otherParams ?? {};
  const response = await endpointFunction({
    parameters: {
      paginationCursor: cursor || undefined,
      paginationDirection:
        paginationArguments.direction || defaultPaginationArguments.direction,
      paginationKeyComponents: paginationArguments.key,
      paginationMode:
        paginationArguments.mode || defaultPaginationArguments.mode,
      paginationSortFieldValues: paginationArguments.sortFields,
      ...(otherParameters && updateQueryParamForApi(otherParams)),
    },
    abortCallbackSetter: (abortCallback) => {
      runInAction(() => {
        currentLoadable.abort = abortCallback;
      });
    },
  });

  if (response.hasError || !response.body) {
    return {
      state: State.Error,
      hasMoreBefore: false,
      hasMoreAfter: false,
    };
  }

  const newWindow = updateWindow(
    {
      hasMoreBefore: currentLoadable.hasMoreBefore,
      items: currentLoadable.data ?? [],
      hasMoreAfter: currentLoadable.hasMoreAfter,
    },
    pageArgs,
    getPageItemFromResponse(response.body.body!)
  );
  return {
    hasMoreBefore: newWindow.hasMoreBefore,
    data: newWindow.items,
    hasMoreAfter: newWindow.hasMoreAfter,
    state: State.Success,
  };
};

export function usePaginatedItemLoader<
  T extends {cursor?: string}[],
  V extends object,
>(fetchCall: PagedFetchCallType<T, V>): PaginatedItemLoaderApi<T, V> {
  const [loadable, setLoadable] = useState<PaginatedLoadable<T>>({
    hasMoreAfter: false,
    hasMoreBefore: false,
    state: State.Loading,
  });

  const loadableRef = useRef(loadable);
  loadableRef.current = loadable;

  const fetchCallRef = useRef(fetchCall);
  fetchCallRef.current = fetchCall;

  const fetch = useCallback(
    async (
      paginationArguments: Omit<PaginationArguments, 'cursor'>,
      additionalProperties: V
    ): Promise<void> => {
      const newLoadable = await fetchCallRef.current(
        paginationArguments,
        {...loadableRef.current, data: undefined, hasMoreBefore: false},
        updateQueryParamForApi(additionalProperties)
      );
      setLoadable(newLoadable);
    },
    []
  );

  const fetchMore = useCallback(
    async (
      paginationArguments: Omit<PaginationArguments, 'cursor'>,
      additionalProperties: V
    ): Promise<void> => {
      const newLoadable = await fetchCallRef.current(
        paginationArguments,
        loadableRef.current,
        updateQueryParamForApi(additionalProperties)
      );
      setLoadable(newLoadable);
    },
    []
  );

  const fetchIds = useCallback(
    (ids: string[] | undefined): Promise<PaginatedLoadable<T>> =>
      fetchCallRef.current(
        {
          mode: PaginationMode.Browse,
          direction: PaginationDirection.Forward,
        },
        {
          state: State.Success,
          hasMoreAfter: false,
          hasMoreBefore: false,
        },
        {searchQuery: '', ids} as V
      ),
    []
  );

  return {loadable, fetch, fetchMore, fetchIds};
}
